import { z } from 'zod';
import { HttpError, WAITLIST_PRIORITIES } from '@vagusx/clients';
import type { AddToWaitlistRequest, UpdateWaitlistRequest, WaitlistEntry } from '@vagusx/clients';

export const waitlistFormSchema = z
  .object({
    patientId: z.string().min(1, 'Choose a patient'),
    appointmentId: z.string().nullable(),
    appointmentTypeId: z.string().nullable(),
    providerId: z.string().nullable(),
    locationId: z.string().nullable(),
    days: z.array(z.number().int().min(0).max(6)),
    earliestTime: z.string().nullable(),
    latestTime: z.string().nullable(),
    priority: z.enum(WAITLIST_PRIORITIES),
    notes: z.string().max(1000),
  })
  .refine((v) => !v.earliestTime || !v.latestTime || v.earliestTime < v.latestTime, {
    path: ['latestTime'],
    message: 'The end must be after the start',
  });

export type WaitlistFormValues = z.infer<typeof waitlistFormSchema>;

/** The API's field names that the form shows an error under. */
const SERVER_FIELD_TO_FORM: Record<string, keyof WaitlistFormValues> = {
  patientId: 'patientId',
  appointmentId: 'appointmentId',
  appointmentTypeId: 'appointmentTypeId',
  providerId: 'providerId',
  locationId: 'locationId',
  priority: 'priority',
  notes: 'notes',
  'preferences.preferredDaysOfWeek': 'days',
  'preferences.earliestTime': 'earliestTime',
  'preferences.latestTime': 'latestTime',
};

/**
 * A refused add or edit's field errors, as form fields (e.g. `locationId` when
 * the EHR needs a location to file the entry, ADR-292). Empty when the error
 * names no field the form has: show its message instead.
 */
export function formFieldErrors(
  err: unknown
): Array<{ field: keyof WaitlistFormValues; message: string }> {
  if (!(err instanceof HttpError) || !err.isValidation) return [];
  const out: Array<{ field: keyof WaitlistFormValues; message: string }> = [];
  for (const fe of err.fieldErrors) {
    const field = SERVER_FIELD_TO_FORM[fe.field];
    if (field && !out.some((e) => e.field === field)) out.push({ field, message: fe.message });
  }
  return out;
}

export function entryToFormValues(entry: WaitlistEntry | null): WaitlistFormValues {
  return {
    patientId: entry?.patient.id ?? '',
    appointmentId: entry?.appointment?.id ?? null,
    appointmentTypeId: entry?.appointmentType?.id ?? null,
    providerId: entry?.provider?.id ?? null,
    locationId: entry?.location?.id ?? null,
    days: entry?.preferences.preferredDaysOfWeek ?? [],
    earliestTime: entry?.preferences.earliestTime ?? null,
    latestTime: entry?.preferences.latestTime ?? null,
    priority: entry?.priority ?? 'normal',
    notes: entry?.notes ?? '',
  };
}

/**
 * The appointment a new entry should start linked to: the patient's only
 * upcoming one, since an entry for them is almost always "move that one
 * sooner". None when they have several or none, or when staff already chose an
 * appointment type (linking would replace it with the appointment's).
 */
export function appointmentToAutoPick<T>(
  appointments: readonly T[],
  typeChosen: boolean
): T | null {
  return appointments.length === 1 && !typeChosen ? appointments[0]! : null;
}

/** The API's preferences from the form; an absent field means any. */
export function formPreferences(values: WaitlistFormValues): {
  preferredDaysOfWeek?: number[];
  earliestTime?: string;
  latestTime?: string;
} {
  return {
    ...(values.days.length > 0
      ? { preferredDaysOfWeek: [...values.days].sort((a, b) => a - b) }
      : {}),
    ...(values.earliestTime ? { earliestTime: values.earliestTime } : {}),
    ...(values.latestTime ? { latestTime: values.latestTime } : {}),
  };
}

/** The add request. A linked appointment brings its own type, so the type is sent only without one. */
export function addPayload(values: WaitlistFormValues): AddToWaitlistRequest {
  const notes = values.notes.trim();
  return {
    patientId: values.patientId,
    ...(values.appointmentId
      ? { appointmentId: values.appointmentId }
      : values.appointmentTypeId
        ? { appointmentTypeId: values.appointmentTypeId }
        : {}),
    ...(values.providerId ? { providerId: values.providerId } : {}),
    ...(values.locationId ? { locationId: values.locationId } : {}),
    preferences: formPreferences(values),
    priority: values.priority,
    ...(notes ? { notes } : {}),
  };
}

/**
 * The edit request. The appointment and type go only when they changed: the
 * API takes the type from a linked appointment and refuses a type-only edit
 * while one is linked. Provider / location go as null for "any".
 */
export function updatePayload(
  entry: WaitlistEntry,
  values: WaitlistFormValues
): UpdateWaitlistRequest {
  const notes = values.notes.trim();
  const appointmentChanged = values.appointmentId !== (entry.appointment?.id ?? null);
  const typeChanged = values.appointmentTypeId !== (entry.appointmentType?.id ?? null);
  return {
    ...(appointmentChanged ? { appointmentId: values.appointmentId } : {}),
    ...(!values.appointmentId && typeChanged
      ? { appointmentTypeId: values.appointmentTypeId }
      : {}),
    providerId: values.providerId,
    locationId: values.locationId,
    preferences: formPreferences(values),
    priority: values.priority,
    notes: notes || null,
  };
}
